// The MP3 export flow: explain, record, encode, save.
//
// This is a modal rather than a plain button because the export is not
// instantaneous or invisible — it records in real time, it needs the user to
// tick an easily-missed checkbox in the browser's share picker, and it can
// fail in ways only the user can fix. See src/mp3.ts for why capture is the
// only route to a file.
import { useEffect, useRef, useState } from 'react';

import { useStore } from '../store';
import {
  Mp3Error,
  type Mp3ErrorCode,
  type Mp3Progress,
  downloadBlob,
  exportSpeechToMp3,
} from '../mp3';

interface Props {
  /** One sentence per entry, already stripped of meanings. */
  lines: string[];
  /** Download filename, without the extension. */
  baseName: string;
  rate: number;
  voiceId?: string;
  onClose: () => void;
}

type Stage = 'intro' | 'running' | 'done' | 'error';

// Rough read-aloud estimate: English TTS averages ~14 characters a second at
// 1x, plus the pause between sentences. Only used to set expectations.
const CHARS_PER_SECOND = 14;
const GAP_MS = 500;

function estimateMinutes(lines: string[], rate: number): number {
  const chars = lines.reduce((n, l) => n + l.length, 0);
  const seconds = chars / CHARS_PER_SECOND / (rate || 1) + (lines.length * GAP_MS) / 1000;
  return Math.max(1, Math.round(seconds / 60));
}

const ERROR_KEY: Record<Mp3ErrorCode, string> = {
  unsupported: 'mp3.err_unsupported',
  denied: 'mp3.err_denied',
  no_audio: 'mp3.err_no_audio',
  silent: 'mp3.err_silent',
  aborted: 'mp3.err_aborted',
  failed: 'mp3.err_failed',
};

export default function Mp3ExportModal({ lines, baseName, rate, voiceId, onClose }: Props) {
  const s = useStore();
  const [stage, setStage] = useState<Stage>('intro');
  const [progress, setProgress] = useState<Mp3Progress | null>(null);
  const [errorCode, setErrorCode] = useState<Mp3ErrorCode>('failed');
  const abortRef = useRef<AbortController | null>(null);
  const fileName = `${baseName}.mp3`;

  // Closing the dialog — including by unmounting — must stop the recording and
  // silence the speech engine, never leave them running in the background.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      abortRef.current?.abort();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const start = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setProgress({ phase: 'speaking', done: 0, total: lines.length });
    setStage('running');
    try {
      const blob = await exportSpeechToMp3(lines, {
        rate,
        voiceId,
        gapMs: GAP_MS,
        signal: controller.signal,
        onProgress: setProgress,
      });
      downloadBlob(blob, fileName);
      setStage('done');
    } catch (e) {
      if (e instanceof Mp3Error && e.code === 'aborted') {
        onClose(); // the user asked to stop; no need to report it back to them
        return;
      }
      console.error('[mp3] export failed', e);
      setErrorCode(e instanceof Mp3Error ? e.code : 'failed');
      setStage('error');
    } finally {
      abortRef.current = null;
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
    onClose();
  };

  return (
    <div className="backdrop backdrop--center">
      <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="mp3-title">
        <h2 className="dialog__title" id="mp3-title">
          {s.t('mp3.title')}
        </h2>

        {stage === 'intro' && (
          <>
            <p className="dialog__body">
              {s.t('mp3.intro_body', {
                count: lines.length,
                minutes: estimateMinutes(lines, rate),
              })}
            </p>
            <p className="hint">{s.t('mp3.intro_howto')}</p>
            <div className="dialog__actions">
              <button type="button" className="btn btn--text" onClick={cancel}>
                {s.t('dialog.cancel')}
              </button>
              <button type="button" className="btn btn--primary" onClick={start} autoFocus>
                {s.t('mp3.start_button')}
              </button>
            </div>
          </>
        )}

        {stage === 'running' && progress && (
          <>
            <p className="dialog__body">
              {progress.phase === 'speaking'
                ? s.t('mp3.phase_speaking', { done: progress.done, total: progress.total })
                : s.t('mp3.phase_encoding', { pct: percent(progress) })}
            </p>
            <div
              className="progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent(progress)}
            >
              <div className="progress__bar" style={{ width: `${percent(progress)}%` }} />
            </div>
            {!!progress.line && <p className="progress__now">“{progress.line}”</p>}
            <p className="hint">{s.t('mp3.keep_quiet')}</p>
            <div className="dialog__actions">
              <button type="button" className="btn btn--text" onClick={cancel}>
                {s.t('dialog.cancel')}
              </button>
            </div>
          </>
        )}

        {stage === 'done' && (
          <>
            <p className="dialog__body">{s.t('mp3.done_body', { name: fileName })}</p>
            <div className="dialog__actions">
              <button type="button" className="btn btn--primary" onClick={onClose} autoFocus>
                {s.t('action.close')}
              </button>
            </div>
          </>
        )}

        {stage === 'error' && (
          <>
            <p className="dialog__body">{s.t(ERROR_KEY[errorCode])}</p>
            <div className="dialog__actions">
              <button type="button" className="btn btn--text" onClick={onClose}>
                {s.t('action.close')}
              </button>
              {errorCode !== 'unsupported' && (
                <button type="button" className="btn btn--primary" onClick={start} autoFocus>
                  {s.t('action.retry')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function percent(p: Mp3Progress): number {
  if (!p.total) return 0;
  return Math.min(100, Math.round((p.done / p.total) * 100));
}
